import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Button, Input, DarkModeToggleCompact } from '../../components/ui'
import { Save, ArrowLeft, Lock, Bell, Copy, ExternalLink, Smartphone, Settings as SettingsIcon, Gamepad, Users, CreditCard, Coins } from 'lucide-react'

import { useTranslation } from 'react-i18next'

export const TournamentSettingsPage = () => {
    const { t } = useTranslation()
    const { tournamentId } = useParams()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [settings, setSettings] = useState({
        name: '',
        referee_pin: '0000',
        theme_color: '#4f46e5',
        push_enabled: true,
        sport_type: 'goals',
        sport_format: 'teams',
        entry_fee: 0,
        payment_enabled: false,
        payment_settings: {
            mollie_api_key: '',
            stripe_connect_id: '',
            payment_methods: ['ideal', 'bancontact', 'creditcard']
        }
    })

    useEffect(() => {
        fetchSettings()
    }, [tournamentId])

    const fetchSettings = async () => {
        const { data } = await supabase
            .from('tournaments')
            .select('name, referee_pin, theme_color, sport_type, sport_format, entry_fee, payment_enabled, payment_settings, settings')
            .eq('id', tournamentId)
            .single()

        if (data) {
            setSettings({
                name: data.name,
                referee_pin: data.referee_pin || '0000',
                theme_color: data.theme_color || '#4f46e5',
                push_enabled: true,
                sport_type: data.sport_type || 'goals',
                sport_format: data.sport_format || 'teams',
                entry_fee: data.entry_fee || 0,
                payment_enabled: !!data.payment_enabled,
                payment_settings: {
                    mollie_api_key: data.payment_settings?.mollie_api_key || '',
                    stripe_connect_id: data.payment_settings?.stripe_connect_id || '',
                    payment_methods: data.payment_settings?.payment_methods || ['ideal', 'bancontact', 'creditcard']
                },
                rules: data.settings?.rules || ''
            })
        }
        setLoading(false)
    }

    const handleSave = async (e) => {
        e.preventDefault()
        setSaving(true)
        try {
            const { error } = await supabase
                .from('tournaments')
                .update({
                    name: settings.name,
                    referee_pin: settings.referee_pin,
                    theme_color: settings.theme_color,
                    sport_type: settings.sport_type,
                    sport_format: settings.sport_format,
                    entry_fee: Number(settings.entry_fee),
                    payment_enabled: settings.payment_enabled,
                    payment_settings: settings.payment_settings,
                    settings: { rules: settings.rules || '' }
                })
                .eq('id', tournamentId)

            if (error) throw error
            alert(t('settings_page.save_success'))
        } catch (err) {
            alert(t('common.error') + ': ' + err.message)
        } finally {
            setSaving(false)
        }
    }

    const handleTestNotification = () => {
        if (Notification.permission === 'granted') {
            new Notification(t('settings_page.test_notification_title'), { body: t('settings_page.test_notification_body_alt'), icon: '/logo-icon.webp' })
        } else {
            Notification.requestPermission().then(perm => {
                if (perm === 'granted') {
                    new Notification(t('settings_page.test_notification_title'), { body: t('settings_page.test_notification_body'), icon: '/logo-icon.webp' })
                }
            })
        }
    }

    const refereeLink = `${window.location.protocol}//${window.location.host}/live/${tournamentId}/referee`
    const isEsports = settings.sport_type === 'esports'

    if (loading) return (
        <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500"></div>
        </div>
    )

    return (
        <div className="min-h-screen">
            <div className="premium-container">
                {/* Hero Section */}
                <div className="mb-12 animate-slideUp">
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate('/admin/dashboard')} 
                        className={`mb-4 ${isEsports ? 'text-slate-300 hover:text-white hover:bg-white/5' : ''}`}
                    >
                        <ArrowLeft size={16} className="mr-2" />
                        {t('settings_page.back_to_dashboard')}
                    </Button>

                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center gap-4">
                            <div className={`p-3.5 rounded-2xl shadow-lg transition-all duration-500 hover:rotate-12 ${
                                isEsports 
                                    ? 'bg-gradient-to-br from-purple-500 to-indigo-600 shadow-[0_0_20px_rgba(168,85,247,0.4)]' 
                                    : 'bg-gradient-to-br from-orange-400 to-orange-600 shadow-[0_0_20px_rgba(249,115,22,0.3)]'
                            }`}>
                                <SettingsIcon className="text-white animate-[spin_8s_linear_infinite]" size={32} />
                            </div>
                            <div>
                                <h1 className={`text-5xl font-bold leading-tight ${isEsports ? 'text-white' : 'text-gray-900'}`}>
                                    {t('settings_page.title')}
                                </h1>
                                <p className={`text-lg mt-1 ${isEsports ? 'text-slate-400' : 'text-gray-600'}`}>{t('settings_page.subtitle')}</p>
                            </div>
                        </div>

                        <DarkModeToggleCompact />
                    </div>
                </div>

                <form onSubmit={handleSave} className="space-y-8 animate-slideUp" style={{ animationDelay: '0.1s' }}>

                    {/* General Settings */}
                    <div className={`glass-strong premium-card rounded-3xl p-8 border transition-all duration-300 ${
                        isEsports 
                            ? 'bg-[#090610]/40 border-purple-500/20 shadow-[0_0_30px_rgba(168,85,247,0.08)]' 
                            : 'bg-white border-gray-100'
                    }`}>
                        <h3 className={`text-2xl font-bold mb-8 flex items-center gap-4 ${isEsports ? 'text-white' : 'text-gray-900'}`}>
                            <div className={`p-2.5 rounded-xl border transition-all duration-300 ${
                                isEsports 
                                    ? 'bg-purple-50/10 text-cyan-400 border-cyan-500/20' 
                                    : 'bg-blue-100 text-blue-600 border-blue-200'
                            }`}>
                                <Smartphone size={22} />
                            </div>
                            {t('settings_page.section_general')}
                        </h3>
                        <Input
                            label={t('settings_page.label_tournament_name')}
                            value={settings.name}
                            onChange={e => setSettings({ ...settings, name: e.target.value })}
                            required
                            className={isEsports ? 'bg-purple-950/20 border-purple-500/25 text-white focus:border-purple-500' : ''}
                        />

                        <div className="mt-4">
                            <label className={`block text-sm font-medium mb-1 ${isEsports ? 'text-purple-300' : 'text-gray-700'}`}>
                                Toernooi Reglementen / Extra Info
                            </label>
                            <textarea
                                value={settings.rules || ''}
                                onChange={e => setSettings({ ...settings, rules: e.target.value })}
                                rows={4}
                                placeholder="Plaats hier reglementen, bijzonderheden, of welkomsttekst..."
                                className={`w-full p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all ${
                                    isEsports 
                                        ? 'bg-purple-950/20 border-purple-500/25 text-white placeholder-purple-300/30' 
                                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                                }`}
                            />
                        </div>

                        {/* Sport Category Selection */}
                        <div className={`mt-8 border-t pt-8 ${isEsports ? 'border-purple-500/10' : 'border-gray-200/20'}`}>
                            <label className={`text-sm font-bold uppercase tracking-wider mb-5 block ${isEsports ? 'text-purple-300' : 'text-gray-700'}`}>
                                {t('modal.category_label')}
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Goals Card */}
                                <div
                                    className={`p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 text-center hover:scale-[1.02] flex flex-col items-center justify-center ${
                                        settings.sport_type === 'goals'
                                            ? isEsports
                                                ? 'border-emerald-500 bg-emerald-950/20 shadow-[0_0_20px_rgba(16,185,129,0.15)] text-slate-100'
                                                : 'border-emerald-500 bg-emerald-50/50 shadow-md'
                                            : isEsports
                                                ? 'border-white/10 bg-white/5 hover:border-white/20'
                                                : 'border-gray-200 bg-transparent hover:border-gray-300'
                                    }`}
                                    onClick={() => setSettings({ ...settings, sport_type: 'goals' })}
                                >
                                    <div className="text-4xl mb-3 filter drop-shadow-md">⚽</div>
                                    <p className="font-bold text-base">{t('modal.sport_field')}</p>
                                    <p className={`text-xs mt-1.5 ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('modal.sport_field_desc')}</p>
                                </div>
                                {/* Sets Card */}
                                <div
                                    className={`p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 text-center hover:scale-[1.02] flex flex-col items-center justify-center ${
                                        settings.sport_type === 'sets'
                                            ? isEsports
                                                ? 'border-amber-500 bg-amber-950/20 shadow-[0_0_20px_rgba(245,158,11,0.15)] text-slate-100'
                                                : 'border-yellow-500 bg-yellow-50/50 shadow-md'
                                            : isEsports
                                                ? 'border-white/10 bg-white/5 hover:border-white/20'
                                                : 'border-gray-200 bg-transparent hover:border-gray-300'
                                    }`}
                                    onClick={() => setSettings({ ...settings, sport_type: 'sets' })}
                                >
                                    <div className="text-4xl mb-3 filter drop-shadow-md">🎾</div>
                                    <p className="font-bold text-base">{t('modal.sport_racket')}</p>
                                    <p className={`text-xs mt-1.5 ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('modal.sport_racket_desc')}</p>
                                </div>
                                {/* Esports Card */}
                                <div
                                    className={`p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 text-center hover:scale-[1.02] flex flex-col items-center justify-center ${
                                        settings.sport_type === 'esports'
                                            ? 'border-purple-500 bg-purple-500/10 shadow-[0_0_25px_rgba(168,85,247,0.25)] text-slate-100'
                                            : isEsports
                                                ? 'border-white/10 bg-white/5 hover:border-white/20'
                                                : 'border-gray-200 bg-transparent hover:border-gray-300'
                                    }`}
                                    onClick={() => setSettings({ ...settings, sport_type: 'esports' })}
                                >
                                    <div className="flex justify-center mb-3 text-purple-500 filter drop-shadow-[0_0_8px_rgba(168,85,247,0.5)]">
                                        <Gamepad size={36} />
                                    </div>
                                    <p className="font-bold text-base">{t('modal.sport_esports')}</p>
                                    <p className={`text-xs mt-1.5 ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('modal.sport_esports_desc')}</p>
                                </div>
                            </div>
                        </div>

                        {/* Format Selection (Only shown when esports is active) */}
                        {settings.sport_type === 'esports' && (
                            <div className="mt-8 border-t border-purple-500/10 pt-8 animate-fadeIn">
                                <label className="text-sm font-bold uppercase tracking-wider mb-5 block text-purple-300">{t('modal.format_label')}</label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div
                                        className={`p-5 rounded-2xl border-2 cursor-pointer transition-all duration-300 flex flex-col items-center text-center gap-3 hover:scale-[1.02] ${
                                            settings.sport_format === 'individual'
                                                ? 'border-purple-500 bg-purple-500/15 shadow-[0_0_20px_rgba(168,85,247,0.2)] text-slate-100'
                                                : isEsports
                                                    ? 'border-white/10 bg-white/5 hover:border-white/15'
                                                    : 'border-gray-200 bg-transparent hover:border-gray-300'
                                        }`}
                                        onClick={() => setSettings({ ...settings, sport_format: 'individual' })}
                                    >
                                        <Users size={28} className="text-purple-400 filter drop-shadow-[0_0_4px_rgba(168,85,247,0.4)]" />
                                        <div>
                                            <p className="font-bold text-base">{t('modal.format_solo')}</p>
                                            <p className={`text-xs mt-1.5 ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('modal.format_solo_desc')}</p>
                                        </div>
                                    </div>
                                    <div
                                        className={`p-5 rounded-2xl border-2 cursor-pointer transition-all duration-300 flex flex-col items-center text-center gap-3 hover:scale-[1.02] ${
                                            settings.sport_format === 'teams'
                                                ? 'border-purple-500 bg-purple-500/15 shadow-[0_0_20px_rgba(168,85,247,0.2)] text-slate-100'
                                                : isEsports
                                                    ? 'border-white/10 bg-white/5 hover:border-white/15'
                                                    : 'border-gray-200 bg-transparent hover:border-gray-300'
                                        }`}
                                        onClick={() => setSettings({ ...settings, sport_format: 'teams' })}
                                    >
                                        <div className="flex -space-x-2">
                                            <Users size={24} className="text-purple-400 filter drop-shadow-[0_0_4px_rgba(168,85,247,0.4)]" />
                                            <Users size={24} className="text-purple-300/80" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-base">{t('modal.format_teams')}</p>
                                            <p className={`text-xs mt-1.5 ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('modal.format_teams_desc')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Referee Settings */}
                    <div className={`glass-strong premium-card rounded-3xl p-8 border transition-all duration-300 relative overflow-hidden ${
                        isEsports 
                            ? 'bg-[#090610]/40 border-purple-500/20 shadow-[0_0_30px_rgba(168,85,247,0.08)]' 
                            : 'bg-white border-gray-100 shadow-sm'
                    }`}>
                        <div className={`absolute top-0 left-0 w-1.5 h-full ${isEsports ? 'bg-cyan-500 shadow-[0_0_10px_#06b6d4]' : 'bg-yellow-400'}`}></div>
                        <h3 className={`text-2xl font-bold mb-4 flex items-center gap-4 ${isEsports ? 'text-white' : 'text-gray-900'}`}>
                            <div className={`p-2.5 rounded-xl border transition-all duration-300 ${
                                isEsports 
                                    ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' 
                                    : 'bg-yellow-100 text-yellow-600 border-yellow-200'
                            }`}>
                                <Lock size={22} />
                            </div>
                            {t('settings_page.section_referee')}
                        </h3>
                        <p className={`mb-6 text-sm ${isEsports ? 'text-slate-400' : 'text-gray-600'}`}>
                            {t('settings_page.referee_desc')}
                        </p>

                        <div className={`p-5 rounded-2xl border mb-6 transition-all duration-300 ${
                            isEsports 
                                ? 'bg-purple-950/20 border-purple-500/25 shadow-[0_0_15px_rgba(168,85,247,0.08)]' 
                                : 'bg-yellow-50/50 border-yellow-200/50'
                        }`}>
                            <label className={`text-xs font-bold uppercase block mb-2 ${isEsports ? 'text-cyan-400' : 'text-yellow-800'}`}>{t('settings_page.label_referee_link')}</label>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <code className={`flex-1 p-3.5 rounded-xl border text-sm font-mono overflow-x-auto shadow-inner ${
                                    isEsports 
                                        ? 'bg-[#0b0716]/60 border-purple-500/20 text-cyan-300' 
                                        : 'bg-white/80 border-yellow-200 text-yellow-900'
                                }`}>
                                    {refereeLink}
                                </code>
                                <div className="flex gap-2 shrink-0">
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            navigator.clipboard.writeText(refereeLink)
                                            alert(t('common.copied') || 'Gekopieerd!')
                                        }}
                                        size="md"
                                        variant="outline"
                                        className={isEsports ? 'border-purple-500/30 text-purple-300 hover:bg-purple-500/10' : ''}
                                        icon={<Copy size={16} />}
                                    >
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={() => window.open(refereeLink, '_blank')}
                                        size="md"
                                        variant="outline"
                                        className={isEsports ? 'border-purple-500/30 text-purple-300 hover:bg-purple-500/10' : ''}
                                        icon={<ExternalLink size={16} />}
                                    >
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="max-w-xs">
                            <Input
                                label={t('settings_page.label_pin')}
                                value={settings.referee_pin}
                                onChange={e => setSettings({ ...settings, referee_pin: e.target.value })}
                                placeholder="0000"
                                maxLength={6}
                                className={`font-mono tracking-widest text-center text-lg ${isEsports ? 'bg-purple-950/20 border-purple-500/25 text-white focus:border-purple-500' : ''}`}
                            />
                        </div>
                    </div>

                    {/* Notification Settings */}
                    <div className={`glass-strong premium-card rounded-3xl p-8 border transition-all duration-300 relative overflow-hidden ${
                        isEsports 
                            ? 'bg-[#090610]/40 border-purple-500/20 shadow-[0_0_30px_rgba(168,85,247,0.08)]' 
                            : 'bg-white border-gray-100 shadow-sm'
                    }`}>
                        <div className={`absolute top-0 left-0 w-1.5 h-full ${isEsports ? 'bg-purple-500 shadow-[0_0_10px_#a855f7]' : 'bg-purple-500'}`}></div>
                        <h3 className={`text-2xl font-bold mb-4 flex items-center gap-4 ${isEsports ? 'text-white' : 'text-gray-900'}`}>
                            <div className={`p-2.5 rounded-xl border transition-all duration-300 ${
                                isEsports 
                                    ? 'bg-purple-50/10 text-purple-400 border-purple-500/20' 
                                    : 'bg-purple-100 text-purple-600 border-purple-200'
                            }`}>
                                <Bell size={22} />
                            </div>
                            {t('settings_page.section_notifications')}
                        </h3>
                        <p className={`mb-6 text-sm ${isEsports ? 'text-slate-400' : 'text-gray-600'}`}>
                            {t('settings_page.notifications_desc')}
                        </p>

                        <div className={`flex items-center justify-between p-5 rounded-2xl mb-6 border transition-all duration-300 ${
                            isEsports 
                                ? 'bg-purple-950/15 border-purple-500/20' 
                                : 'bg-gray-50/80 border-gray-100'
                        }`}>
                            <div>
                                <span className={`font-bold block mb-1 ${isEsports ? 'text-slate-200' : 'text-gray-800'}`}>{t('settings_page.label_notifications_active')}</span>
                                <p className={`text-xs ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('settings_page.notifications_hint')}</p>
                            </div>
                            <div 
                                className="relative inline-flex items-center cursor-pointer select-none"
                                onClick={() => setSettings(s => ({ ...s, push_enabled: !s.push_enabled }))}
                            >
                                <div className={`w-14 h-8 rounded-full p-1 transition-colors duration-300 shadow-inner relative flex items-center ${
                                    settings.push_enabled 
                                        ? isEsports 
                                            ? 'bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.4)]' 
                                            : 'bg-green-500' 
                                        : 'bg-slate-700/50'
                                }`}>
                                    <div className={`w-6 h-6 rounded-full bg-white shadow-md transform transition-transform duration-300 ${
                                        settings.push_enabled ? 'translate-x-6' : 'translate-x-0'
                                    }`} />
                                </div>
                            </div>
                        </div>

                        <Button 
                            type="button" 
                            onClick={handleTestNotification} 
                            variant="secondary" 
                            className={`w-full ${isEsports ? 'border-purple-500/30 text-purple-300 hover:bg-purple-500/10' : ''}`}
                            icon={<Bell size={16} />}
                        >
                            {t('settings_page.btn_test_notification')}
                        </Button>
                    </div>

                    {/* Payment Settings Card */}
                    <div className={`glass-strong premium-card rounded-3xl p-8 border transition-all duration-300 relative overflow-hidden ${
                        isEsports 
                            ? 'bg-[#090610]/40 border-purple-500/20 shadow-[0_0_30px_rgba(168,85,247,0.08)]' 
                            : 'bg-white dark:bg-[#130f22] border-gray-100 dark:border-white/5 shadow-sm'
                    }`}>
                        <div className={`absolute top-0 left-0 w-1.5 h-full ${isEsports ? 'bg-emerald-500 shadow-[0_0_10px_#10b981]' : 'bg-emerald-500'}`}></div>
                        <h3 className={`text-2xl font-bold mb-4 flex items-center gap-4 ${isEsports ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                            <div className={`p-2.5 rounded-xl border transition-all duration-300 ${
                                isEsports 
                                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                                    : 'bg-emerald-100 text-emerald-600 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20'
                            }`}>
                                <CreditCard size={22} />
                            </div>
                            Inschrijving & Betaling
                        </h3>
                        <p className={`mb-6 text-sm ${isEsports ? 'text-slate-400' : 'text-gray-600 dark:text-slate-300'}`}>
                            Laat deelnemende teams direct betalen via Mollie (iDEAL, Bancontact) bij het online inschrijven. Dit voorkomt handmatig werk en achterstallige betalingen.
                        </p>

                        <div className={`flex items-center justify-between p-5 rounded-2xl mb-6 border transition-all duration-300 ${
                            settings.payment_enabled 
                                ? isEsports ? 'bg-emerald-950/20 border-emerald-500/30' : 'bg-emerald-50/50 dark:bg-emerald-950/10 border-emerald-200 dark:border-emerald-500/20'
                                : isEsports ? 'bg-purple-950/15 border-purple-500/20' : 'bg-gray-50/80 border-gray-100 dark:bg-[#161224] dark:border-white/5'
                        }`}>
                            <div>
                                <span className={`font-bold block mb-1 ${isEsports ? 'text-slate-200' : 'text-gray-800 dark:text-slate-100'}`}>Betaalde inschrijvingen activeren</span>
                                <p className={`text-xs ${isEsports ? 'text-slate-400' : 'text-gray-500 dark:text-slate-400'}`}>Als dit aan staat, moeten teams direct afrekenen om zich in te schrijven.</p>
                            </div>
                            <div 
                                className="relative inline-flex items-center cursor-pointer select-none"
                                onClick={() => setSettings(s => ({ ...s, payment_enabled: !s.payment_enabled }))}
                            >
                                <div className={`w-14 h-8 rounded-full p-1 transition-colors duration-300 shadow-inner relative flex items-center ${
                                    settings.payment_enabled 
                                        ? 'bg-emerald-500' 
                                        : 'bg-slate-700/50'
                                }`}>
                                    <div className={`w-6 h-6 rounded-full bg-white shadow-md transform transition-transform duration-300 ${
                                        settings.payment_enabled ? 'translate-x-6' : 'translate-x-0'
                                    }`} />
                                </div>
                            </div>
                        </div>

                        {settings.payment_enabled && (
                            <div className="space-y-6 animate-fadeIn">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className={`text-sm font-bold block mb-2 ${isEsports ? 'text-slate-300' : 'text-gray-700 dark:text-slate-300'}`}>Inschrijfgeld (€)</label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-slate-400 font-medium">€</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                placeholder="15.00"
                                                value={settings.entry_fee || ''}
                                                onChange={e => setSettings({ ...settings, entry_fee: e.target.value })}
                                                className={`w-full pl-8 pr-4 py-2.5 rounded-xl border outline-none text-base transition-all ${
                                                    isEsports 
                                                        ? 'bg-purple-950/20 border-purple-500/25 text-white focus:border-purple-500' 
                                                        : 'bg-white dark:bg-slate-950/40 border-gray-300 dark:border-white/10 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500'
                                                }`}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className={`text-sm font-bold block mb-2 ${isEsports ? 'text-slate-300' : 'text-gray-700 dark:text-slate-300'}`}>Mollie API Sleutel (Test of Live)</label>
                                        <input
                                            type="password"
                                            placeholder="test_... of live_..."
                                            value={settings.payment_settings?.mollie_api_key || ''}
                                            onChange={e => setSettings({
                                                ...settings,
                                                payment_settings: {
                                                    ...settings.payment_settings,
                                                    mollie_api_key: e.target.value
                                                }
                                            })}
                                            className={`w-full px-4 py-2.5 rounded-xl border outline-none text-base transition-all font-mono ${
                                                isEsports 
                                                    ? 'bg-purple-950/20 border-purple-500/25 text-white focus:border-purple-500' 
                                                    : 'bg-white dark:bg-slate-950/40 border-gray-300 dark:border-white/10 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500'
                                            }`}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className={`text-sm font-bold block mb-3 ${isEsports ? 'text-slate-300' : 'text-gray-700 dark:text-slate-300'}`}>Geactiveerde Betaalmethodes</label>
                                    <div className="flex flex-wrap gap-4">
                                        {['ideal', 'bancontact', 'creditcard'].map(method => {
                                            const active = settings.payment_settings?.payment_methods?.includes(method);
                                            return (
                                                <button
                                                    key={method}
                                                    type="button"
                                                    onClick={() => {
                                                        const current = settings.payment_settings?.payment_methods || [];
                                                        const next = current.includes(method)
                                                            ? current.filter(m => m !== method)
                                                            : [...current, method];
                                                        setSettings({
                                                            ...settings,
                                                            payment_settings: {
                                                                ...settings.payment_settings,
                                                                payment_methods: next
                                                            }
                                                        });
                                                    }}
                                                    className={`px-4 py-2 rounded-xl font-medium border text-sm transition-all flex items-center gap-2 ${
                                                        active 
                                                            ? 'bg-emerald-500 text-white border-emerald-600 shadow-md' 
                                                            : isEsports 
                                                                ? 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
                                                                : 'bg-gray-50 dark:bg-slate-800/40 border-gray-200 dark:border-white/5 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-850'
                                                    }`}
                                                >
                                                    {method === 'ideal' && '🏦 iDEAL'}
                                                    {method === 'bancontact' && '🇧🇪 Bancontact'}
                                                    {method === 'creditcard' && '💳 Creditcard'}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>

                                <div className={`p-4 rounded-xl border text-xs flex gap-3 items-start ${
                                    isEsports ? 'bg-emerald-950/10 border-emerald-500/20 text-slate-300' : 'bg-emerald-50 dark:bg-emerald-950/10 border-emerald-100 dark:border-emerald-500/20 text-emerald-800 dark:text-emerald-300'
                                }`}>
                                    <Coins className="shrink-0 text-emerald-500" size={18} />
                                    <div>
                                        <span className="font-bold block mb-1">Tip voor de Toernooiorganisator</span>
                                        Gebruik voor het testen een Mollie Test API Sleutel (begint met `test_`). Teams krijgen dan een gesimuleerde betaling te zien om de inschrijf-flow veilig te testen zonder echt geld te betalen.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end pt-4 pb-12">
                        <Button 
                            type="submit" 
                            size="lg" 
                            disabled={saving} 
                            variant="primary" 
                            className="shadow-xl px-8" 
                            icon={<Save size={20} />}
                        >
                            {saving ? t('common.saving') : t('settings_page.btn_save')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    )
}
